import { toast } from 'sonner'
import { create } from 'zustand'

import {
  type BookAiStatus,
  type BookChatMessage,
  clearBookChat,
  getBookAiStatus,
  getBookChat,
  postBookChat,
  requestBookAiAnalysis,
} from '@/api/services/book-ai'

/**
 * リーダーで開いている本の AI 読書アシスト（解析結果・解析ジョブ・チャット）
 *
 * 一度に開く本は 1 冊なので、episodeId が変わったら中身を入れ替える。
 * 応答が返る前に別の本へ移った場合は、古い応答を捨てる。
 */
type BookAiStore = {
  episodeId: number | null
  status: BookAiStatus | null
  isStatusLoading: boolean
  messages: BookChatMessage[]
  isChatLoaded: boolean
  isSending: boolean
  /** 本文で選択して「AIに質問」した箇所（質問タブの入力欄に引用として付ける） */
  chatQuote: string | null
  setChatQuote: (quote: string | null) => void
  open: (episodeId: number) => Promise<void>
  refreshStatus: () => Promise<void>
  startAnalysis: () => Promise<void>
  loadChat: () => Promise<void>
  sendQuestion: (question: string, untilKey: number | null) => Promise<boolean>
  clearChat: () => Promise<void>
}

// 本文で選んだ箇所を引用するときの文字数の上限（質問全体はサーバー側で 1000 字まで）
const MAX_QUOTE_CHARS = 300

export const isBookAiJobActive = (status: BookAiStatus | null): boolean =>
  status?.job?.status === 'pending' || status?.job?.status === 'running'

export const useBookAiStore = create<BookAiStore>((set, get) => ({
  episodeId: null,
  status: null,
  isStatusLoading: false,
  messages: [],
  isChatLoaded: false,
  isSending: false,
  chatQuote: null,

  setChatQuote: (quote) => {
    const trimmed = quote?.trim()
    if (!trimmed) {
      set({ chatQuote: null })
      return
    }
    set({ chatQuote: trimmed.length > MAX_QUOTE_CHARS ? `${trimmed.slice(0, MAX_QUOTE_CHARS)}…` : trimmed })
  },

  open: async (episodeId) => {
    // 同じ本を開き直したときも、解析が進んでいるかもしれないので取り直す
    if (get().episodeId !== episodeId) {
      set({ episodeId, status: null, messages: [], isChatLoaded: false, isSending: false, chatQuote: null })
    }
    await get().refreshStatus()
  },

  refreshStatus: async () => {
    const episodeId = get().episodeId
    if (episodeId == null) return
    set({ isStatusLoading: true })
    try {
      const status = await getBookAiStatus(episodeId)
      if (get().episodeId !== episodeId) return
      const previous = get().status
      set({ status })
      // 解析が終わった瞬間だけ知らせる（開いた時点で終わっていたものは知らせない）
      const finishedNow =
        isBookAiJobActive(previous) && !isBookAiJobActive(status) && previous?.job?.id === status.job?.id
      if (finishedNow && status.job?.status === 'completed') toast.success('AI 解析が完了しました')
      if (finishedNow && status.job?.status === 'failed') toast.error(status.job.message || 'AI 解析に失敗しました')
    } catch (error) {
      console.error('AI 読書アシストの状態を取得できませんでした:', error)
    } finally {
      if (get().episodeId === episodeId) set({ isStatusLoading: false })
    }
  },

  startAnalysis: async () => {
    const episodeId = get().episodeId
    if (episodeId == null) return
    try {
      const job = await requestBookAiAnalysis(episodeId)
      if (get().episodeId !== episodeId) return
      set((state) => ({ status: state.status ? { ...state.status, job } : state.status }))
      await get().refreshStatus()
    } catch (error) {
      console.error('AI 解析を開始できませんでした:', error)
    }
  },

  loadChat: async () => {
    const episodeId = get().episodeId
    if (episodeId == null || get().isChatLoaded) return
    try {
      const messages = await getBookChat(episodeId)
      if (get().episodeId !== episodeId) return
      set({ messages, isChatLoaded: true })
    } catch (error) {
      console.error('チャットの履歴を取得できませんでした:', error)
    }
  },

  sendQuestion: async (question, untilKey) => {
    const episodeId = get().episodeId
    if (episodeId == null || get().isSending) return false
    set({ isSending: true })
    try {
      const { question: asked, answer } = await postBookChat(episodeId, question, untilKey)
      if (get().episodeId !== episodeId) return false
      set((state) => ({ messages: [...state.messages, asked, answer] }))
      return true
    } catch (error) {
      console.error('質問を送れませんでした:', error)
      return false
    } finally {
      if (get().episodeId === episodeId) set({ isSending: false })
    }
  },

  clearChat: async () => {
    const episodeId = get().episodeId
    if (episodeId == null) return
    try {
      await clearBookChat(episodeId)
      if (get().episodeId !== episodeId) return
      set({ messages: [] })
    } catch (error) {
      console.error('チャットの履歴を消せませんでした:', error)
    }
  },
}))
